import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  #purchaseButtonObserver = null;

  #observedPurchaseButton = null;

  #refreshFrame = 0;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    document.addEventListener('joa:cart-feedback', this.#scheduleRefresh, { signal });
    document.addEventListener('joa:options-designed', this.#scheduleRefresh, { signal });
    document.addEventListener('joa:price-verified', this.#scheduleRefresh, { signal });
    document.addEventListener('change', this.#scheduleRefresh, { signal });
    window.addEventListener('scroll', this.#scheduleRefresh, { signal, passive: true });
    window.addEventListener('resize', this.#scheduleRefresh, { signal, passive: true });
    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#purchaseButtonObserver?.disconnect();
    this.#abortController.abort();
    cancelAnimationFrame(this.#refreshFrame);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#scheduleRefresh);
    this.#purchaseButtonObserver = new MutationObserver(this.#scheduleRefresh);
    this.#purchaseButtonObserver.observe(productForm.closest('.product-details') || productForm, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'hidden', 'style', 'class'],
    });
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(([entry]) => {
        this.#hiddenByBottom = entry.isIntersecting;
        this.#scheduleRefresh();
      });
      this.#mainBottomObserver.observe(footer);
    }
    this.#refreshPurchase();
  }

  #scheduleRefresh = () => {
    if (this.#refreshFrame) return;
    this.#refreshFrame = requestAnimationFrame(() => {
      this.#refreshFrame = 0;
      if (this.isConnected) this.#refreshPurchase();
    });
  };

  #refreshPurchase() {
    const button = this.#getActiveAddToCartButton();
    if (button !== this.#observedPurchaseButton) {
      this.#buyButtonsIntersectionObserver?.disconnect();
      if (button) this.#buyButtonsIntersectionObserver?.observe(button);
      this.#observedPurchaseButton = button;
    }
    this.#syncPurchaseAction();
    this.#syncSelection();
    const rect = button?.getBoundingClientRect();
    const header = document.querySelector('#header-component')?.getBoundingClientRect();
    const viewportTop = Math.max(0, header?.bottom || 0);
    const inView = rect && rect.top < window.innerHeight && rect.bottom > viewportTop;
    if (!rect || !rect.width || !rect.height || inView || this.#hiddenByBottom || this.#isChatActive()) {
      if (this.#isStuck) this.#hideStickyBar();
    } else if (!this.#isStuck) {
      this.#showStickyBar();
    }
  }

  #syncPurchaseAction() {
    const action = this.refs.addToCartButton;
    const nativeButton = this.#getActiveAddToCartButton();
    const busy = action.dataset.joaCartState === 'loading' || nativeButton?.getAttribute('aria-busy') === 'true';
    const disabled = !nativeButton || nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true' || busy || action.dataset.joaCartState === 'success';
    if (action.disabled !== disabled) action.disabled = disabled;
    if (action.getAttribute('aria-busy') !== String(busy)) action.setAttribute('aria-busy', String(busy));
    const label = action.querySelector('[data-joa-sticky-action]');
    const available = this.dataset.variantAvailable === 'true';
    const purchaseLabel = this.#getProductForm()?.querySelector('form[data-joa-purchase-label]')?.dataset.joaPurchaseLabel;
    const text = available ? purchaseLabel || action.dataset.addToCartText : action.dataset.unavailableText;
    if (label && text && label.textContent !== text) label.textContent = text;
  }

  #syncSelection() {
    const details = this.#getProductForm()?.closest('.product-details');
    const summary = details?.querySelector('.joa-design-summary');
    const target = this.querySelector('.joa-sticky-selection__value');
    if (!target || !details) return;
    const clean = (node) => node?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const priceHost = details.querySelector('product-price');
    const price = clean(priceHost?.querySelector('.price__sale:not(.price__hidden) .price-item--sale') || priceHost?.querySelector('.price__regular .price') || priceHost?.querySelector('.price'));
    const stickyPrice = this.querySelector('.sticky-add-to-cart__price');
    const display = window.JoaPriceDisplay?.read(summary?.querySelector('.joa-design-summary__price')) || window.JoaPriceDisplay?.read(priceHost);
    if (stickyPrice && display?.current) window.JoaPriceDisplay.render(stickyPrice, display);
    else if (price && stickyPrice && clean(stickyPrice) !== price) stickyPrice.textContent = price;
    const summaryOptions = summary?.querySelector('.joa-design-summary__options');
    const summaryText = clean(summary);
    let values = summaryOptions ? clean(summaryOptions).split(' · ').filter(Boolean) : summaryText ? summaryText.split(' · ').slice(0, -1) :
      [...details.querySelectorAll('variant-picker input:checked, variant-picker select')]
        .map((input) => {
          if (!(input instanceof HTMLSelectElement)) return input.value;
          const label = details.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.textContent?.trim() || '';
          const value = clean(input.selectedOptions[0]);
          return /ring size/i.test(label) ? `${label}: ${/not sure/i.test(value) ? summary?.dataset.joaConfirmLater || this.dataset.sizeToConfirm : value}` : value;
        }).filter((value) => value && value !== this.dataset.defaultVariantTitle);
    const ringSizeHeading = details?.querySelector('.joa-option--ring-size .joa-option-heading-main > .mw-text--style-strong');
    const ringSizeLabel = Array.from(ringSizeHeading?.childNodes || []).filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join('').trim();
    if (summary?.classList.contains('joa-config-total') && values.length === 4 && details.querySelector('.joa-option--diamond-size') && details.querySelector('.joa-option--clarity')) {
      const [diamond, clarity, material, size] = values;
      const ringSizeInput = details.querySelector('.joa-option--ring-size select:not(.joa-option-quick-select)');
      const compactSize = /not sure/i.test(clean(ringSizeInput?.selectedOptions[0])) ? `${ringSizeLabel || this.dataset.sizeToConfirm}: ${summary.dataset.joaConfirmLater || this.dataset.sizeToConfirm}` : size;
      values = [diamond.replace(/\s+ct$/i, 'ct'), clarity.replace(/\s*\/\s*/g, ' '), material, compactSize];
    }
    const selections = values.map((value) => value === this.dataset.sizeToConfirm ? `${ringSizeLabel || this.dataset.sizeToConfirm}: ${summary?.dataset.joaConfirmLater || this.dataset.sizeToConfirm}` : value);
    const text = selections.join(' · ');
    const pairedRingOptions = selections.length === 4 && details.querySelector('.joa-option--diamond-size') && details.querySelector('.joa-option--clarity');
    const lines = pairedRingOptions ? [selections.slice(0, 2).join(' · '), selections.slice(2).join(' · ')] : [text];
    const layoutKey = JSON.stringify(lines);
    if (target.dataset.selectionLayout !== layoutKey) {
      const rows = lines.map((line) => {
        const row = document.createElement('span');
        row.className = 'joa-sticky-selection__line';
        row.textContent = line;
        return row;
      });
      target.replaceChildren(...rows.flatMap((row, index) => index ? [document.createTextNode(' '), row] : [row]));
      target.dataset.selectionLayout = layoutKey;
      target.classList.toggle('joa-sticky-selection__value--paired', Boolean(pairedRingOptions));
    }
    const selection = target.closest('.joa-sticky-selection');
    if (selection) selection.hidden = !text;
  }

  handleAddToCartClick = (event) => {
    event?.preventDefault();
    const button = this.#getActiveAddToCartButton();
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true') return;
    this.#targetAddToCartButton = button;
    button.dataset.puppet = 'true';
    button.click();
    this.#scheduleRefresh();
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Returns the customer-visible purchase button. Product-option apps retain
   * Shopify's original button in the DOM and render their validated clone;
   * forwarding to the clone preserves required-field feedback.
   * @returns {HTMLButtonElement | null}
   */
  #getActiveAddToCartButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return null;

    const candidates = Array.from(
      productForm.querySelectorAll('.mw-apo-add-to-cart-cloned, button[data-testid="standalone-add-to-cart"], [ref="addToCartButton"]')
    );

    const visibleButton = candidates.find(
      (candidate) =>
        candidate instanceof HTMLButtonElement &&
        !candidate.closest('.joa-express-form') &&
        candidate.getClientRects().length > 0 &&
        getComputedStyle(candidate).visibility !== 'hidden'
    );

    return visibleButton instanceof HTMLButtonElement ? visibleButton : null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    this.#syncPurchaseAction();
    this.#syncSelection();

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

